import React, { useState, useEffect } from "react";
import { Modal, Tooltip, message, Spin } from "antd";
import { ChevronDown, ChevronUp, MessageCircleQuestion, X, Search, Edit3, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_FAQS } from "../../../services/userAPI";
import { ADD_UPDATE_CLAN_FAQS } from "../../../services/faqMutation";

const AdminFAQPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { accountId, roleId, accountName, name } = location.state || {};

    const [faqActiveTab, setFaqActiveTab] = useState("CLAN");
    const [faqSearchTerm, setFaqSearchTerm] = useState("");
    const [showAddFaqModal, setShowAddFaqModal] = useState(false);
    const [newFaq, setNewFaq] = useState({ question: "", answer: "", product: "DARTS" });
    const [expandedFaqId, setExpandedFaqId] = useState(null);
    const [showEditFaqModal, setShowEditFaqModal] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingFaq, setDeletingFaq] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 5;

    // Fetch FAQ data from API using lazy query for better control
    const [fetchFaqs, { data: apiData, loading: apiLoading, error: apiError }] = useLazyQuery(GET_FAQS, {
        fetchPolicy: "network-only"
    });

    // Separate query to fetch the other tab's count
    const otherTabRef = React.useRef(faqActiveTab === "DARTS" ? "CLAN" : "DARTS");
    const [fetchOtherTabCount] = useLazyQuery(GET_FAQS, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            if (data?.getFaqs?.pageMeta?.totalCount !== undefined) {
                setFaqTotalCounts(prev => ({
                    ...prev,
                    [otherTabRef.current]: data.getFaqs.pageMeta.totalCount
                }));
            }
        }
    });

    // Helper to refetch current tab + other tab's count
    const refetchAllFaqs = () => {
        const otherTab = faqActiveTab === "DARTS" ? "CLAN" : "DARTS";
        otherTabRef.current = otherTab;
        fetchFaqs({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                searchTerm: faqSearchTerm || null,
                faqModule: faqActiveTab,
                pagingCriteria: {
                    limit: PAGE_SIZE,
                    page: currentPage,
                    sortOrder: "asc",
                    sortByField: "created_at"
                }
            }
        });
        fetchOtherTabCount({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                searchTerm: "",
                faqModule: otherTab,
                pagingCriteria: {
                    limit: 1,
                    page: 1,
                    sortOrder: "asc",
                    sortByField: "created_at"
                }
            }
        });
    };

    // Fetch data when page or search changes
    useEffect(() => {
        fetchFaqs({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                searchTerm: faqSearchTerm || null,
                faqModule: faqActiveTab,
                pagingCriteria: {
                    limit: PAGE_SIZE,
                    page: currentPage,
                    sortOrder: "asc",
                    sortByField: "created_at"
                }
            }
        });
    }, [currentPage, faqSearchTerm, fetchFaqs, accountId, roleId, faqActiveTab]);

    // Fetch the other tab's count on mount and when tab changes
    useEffect(() => {
        const otherTab = faqActiveTab === "DARTS" ? "CLAN" : "DARTS";
        otherTabRef.current = otherTab;
        fetchOtherTabCount({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                searchTerm: "",
                faqModule: otherTab,
                pagingCriteria: {
                    limit: 1,
                    page: 1,
                    sortOrder: "asc",
                    sortByField: "created_at"
                }
            }
        });
    }, [faqActiveTab, fetchOtherTabCount, accountId, roleId]);

    // FAQ data state
    const [faqData, setFaqData] = useState({
        DARTS: [],
        CLAN: [],
    });

    // Total counts for each tab (from API pageMeta)
    const [faqTotalCounts, setFaqTotalCounts] = useState({
        DARTS: 0,
        CLAN: 0,
    });

    // Map API data to FAQ structure
    useEffect(() => {
        if (apiData?.getFaqs?.data) {
            console.log("API Data received:", apiData.getFaqs.data);
            const faqs = [];

            apiData.getFaqs.data.forEach(faq => {
                // Check for active status (can be 1 or "1" or true)
                const isActive = faq.status === 1 || faq.status === "1" || faq.status === true;

                if (isActive) {
                    const mappedFaq = {
                        id: faq.id,
                        question: faq.faqParameter || "",
                        answer: faq.faqValue || "",
                        lang: faq.lang || "en",
                        faqtype: faq.faqtype,
                        accountId: faq.accountId,
                        roleIds: faq.roleIds || []
                    };
                    faqs.push(mappedFaq);
                }
            });

            console.log("Mapped FAQ data:", faqs);
            // Set data for the current active tab since API already filters by faqModule
            setFaqData(prev => ({
                ...prev,
                [faqActiveTab]: faqs
            }));
            // Update total count for the active tab from pageMeta
            if (apiData?.getFaqs?.pageMeta?.totalCount !== undefined) {
                setFaqTotalCounts(prev => ({
                    ...prev,
                    [faqActiveTab]: apiData.getFaqs.pageMeta.totalCount
                }));
            }
        }
    }, [apiData, faqActiveTab]);

    // Pagination info from API
    const pageMeta = apiData?.getFaqs?.pageMeta;
    const hasNextPage = pageMeta?.hasNextPage || false;
    const hasPreviousPage = pageMeta?.hasPreviousPage || false;
    const totalCount = pageMeta?.totalCount;
    const totalPages = pageMeta?.totalPages || 1;

    // Pagination handlers
    const handleNextPage = () => {
        if (hasNextPage) {
            setCurrentPage(prev => prev + 1);
            setExpandedFaqId(null);
        }
    };

    const handlePreviousPage = () => {
        if (hasPreviousPage) {
            setCurrentPage(prev => prev - 1);
            setExpandedFaqId(null);
        }
    };

    // Reset pagination when search or tab changes
    useEffect(() => {
        setCurrentPage(1);
    }, [faqSearchTerm, faqActiveTab]);

    const filteredFaqs = faqData[faqActiveTab] || [];

    // Add FAQ mutation
    const [addUpdateFaq, { loading: addFaqLoading }] = useMutation(ADD_UPDATE_CLAN_FAQS, {
        onCompleted: (data) => {
            if (data?.addUpdateClanFaqs?.success) {
                message.success(data.addUpdateClanFaqs.message || "FAQ added successfully!");
                setNewFaq({ question: "", answer: "", product: "DARTS" });
                setShowAddFaqModal(false);
                // Refetch FAQs and counts for both tabs
                refetchAllFaqs();
            } else {
                message.error(data?.addUpdateClanFaqs?.message || "Failed to add FAQ");
            }
        },
        onError: (error) => {
            message.error(error.message || "Failed to add FAQ");
        }
    });

    const handleAddFaq = () => {
        if (!newFaq.question.trim() || !newFaq.answer.trim()) {
            message.error("Please fill in both question and answer");
            return;
        }
        
        addUpdateFaq({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                faqValue: newFaq.answer.trim(),
                faqParameter: newFaq.question.trim(),
                faqId: null,
                faqLabel: newFaq.product
            }
        });
    };

    // Edit FAQ mutation
    const [editFaq, { loading: editFaqLoading }] = useMutation(ADD_UPDATE_CLAN_FAQS, {
        onCompleted: (data) => {
            if (data?.addUpdateClanFaqs?.success) {
                message.success(data.addUpdateClanFaqs.message || "FAQ updated successfully!");
                setEditingFaq(null);
                setShowEditFaqModal(false);
                // Refetch FAQs and counts for both tabs
                refetchAllFaqs();
            } else {
                message.error(data?.addUpdateClanFaqs?.message || "Failed to update FAQ");
            }
        },
        onError: (error) => {
            message.error(error.message || "Failed to update FAQ");
        }
    });

    const handleEditFaq = () => {
        if (!editingFaq || !editingFaq.question.trim() || !editingFaq.answer.trim()) {
            message.error("Please fill in both question and answer");
            return;
        }
        
        editFaq({
            variables: {
                accountId: editingFaq.accountId ? parseInt(editingFaq.accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                faqValue: editingFaq.answer.trim(),
                faqParameter: editingFaq.question.trim(),
                faqId: parseInt(editingFaq.id),
                faqLabel: faqActiveTab
            }
        });
    };

    // Delete FAQ mutation
    const [deleteFaq, { loading: deleteFaqLoading }] = useMutation(ADD_UPDATE_CLAN_FAQS, {
        onCompleted: (data) => {
            if (data?.addUpdateClanFaqs?.success) {
                message.success("FAQ deleted successfully!");
                setDeletingFaq(null);
                setShowDeleteConfirm(false);
                setExpandedFaqId(null);
                // Refetch FAQs and counts for both tabs
                refetchAllFaqs();
            } else {
                message.error(data?.addUpdateClanFaqs?.message || "Failed to delete FAQ");
            }
        },
        onError: (error) => {
            message.error(error.message || "Failed to delete FAQ");
        }
    });

    const handleDeleteFaq = () => {
        if (!deletingFaq) return;
        
        deleteFaq({
            variables: {
                accountId: deletingFaq.accountId ? parseInt(deletingFaq.accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                faqValue: deletingFaq.answer,
                faqParameter: deletingFaq.question,
                faqId: parseInt(deletingFaq.id),
                faqLabel: faqActiveTab,
                status: 0
            }
        });
    };

    const openEditModal = (faq, e) => {
        e.stopPropagation();
        setEditingFaq({ ...faq });
        setShowEditFaqModal(true);
    };

    const openDeleteConfirm = (faq, e) => {
        e.stopPropagation();
        setDeletingFaq(faq);
        setShowDeleteConfirm(true);
    };

    const productTabs = ["DARTS", "CLAN"];

    const handleBack = () => {
        navigate("/view-role", {
            state: { accountId, roleId, accountName, name }
        });
    };

    const handleBackToAdmin = () => {
        navigate("/admin"); // This will navigate back to the previous page
    }

    return (
        <div className="min-h-screen bg-gray-50" >
            <div className="user-profile-container">
                {/* Breadcrumb and Back Button */}
                <div className="mx-auto px-3">
                    <div className="user-management-header">
                        <div className="user-management-breadcrumb">
                            <span
                                onClick={handleBackToAdmin}
                                style={{ fontFamily: "Inter", cursor: "pointer" }}
                            >
                                {accountName}
                            </span>
                            <span>&gt;</span>
                            <span
                                onClick={handleBack}
                                style={{ fontFamily: "Inter", cursor: "pointer" }}
                            >
                                {name}
                            </span>
                            <span>&gt;</span>
                            <span style={{ fontWeight: "bold" }} className="bold-text">FAQ's</span>
                        </div>
                        <div>
                            <button className="back-button" onClick={handleBackToAdmin}>
                                Back to Home
                            </button>
                        </div>
                    </div>
                </div>

                {/* Two Column Layout */}
                <div className="mx-auto px-3 pb-6 mt-1">
                    <div className="grid grid-cols-12 gap-6">
                        {/* Left Sidebar - Product Selector & Stats */}
                        <div className="col-span-3">
                            <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
                                {/* Product Tabs - Vertical */}
                                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Products</h3>
                                    <div className="space-y-2">
                                        {productTabs.map((tab) => (
                                            <div
                                                key={tab}
                                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${faqActiveTab === tab
                                                    ? 'bg-[#AD0101] shadow-md'
                                                    : 'bg-white hover:bg-gray-100 border border-gray-200'
                                                    }`}
                                                onClick={() => {
                                                    setFaqActiveTab(tab);
                                                    setExpandedFaqId(null);
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {/* <img src={getProductIcon(tab)} alt={tab} className={`w-5 h-5 object-contain ${faqActiveTab === tab ? 'brightness-0 invert' : ''}`} /> */}
                                                    <span className={`text-sm font-semibold ${faqActiveTab === tab ? 'text-white' : 'text-gray-700'}`}>
                                                        {tab}
                                                    </span>
                                                </div>
                                                <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${faqActiveTab === tab ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
                                                    {faqTotalCounts[tab] || 0}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Stats Card */}
                                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-4 border border-red-100">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-[#AD0101] mb-1">
                                            {(faqTotalCounts.DARTS || 0) + (faqTotalCounts.CLAN || 0)}
                                        </div>
                                        <div className="text-xs text-gray-600 font-medium">
                                            Total FAQs
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Content - Search & FAQs */}
                        <div className="col-span-9">
                            <div className="bg-white rounded-xl p-5 shadow-sm space-y-4">
                                {/* Search Bar with Add FAQ Button */}
                                <div className="flex items-center gap-3">
                                    <div className="relative flex-1">
                                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search questions..."
                                            value={faqSearchTerm}
                                            onChange={(e) => setFaqSearchTerm(e.target.value)}
                                            className="w-full py-2 pl-10 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:bg-white focus:ring-2 focus:ring-[#F9FAFB]/20 transition-all"
                                        />
                                        {faqSearchTerm && (
                                            <button
                                                onClick={() => setFaqSearchTerm("")}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        className="flex items-center gap-2 px-4 py-2 bg-[#AD0101] text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
                                        onClick={() => setShowAddFaqModal(true)}
                                    >
                                        Add FAQ
                                    </button>
                                </div>

                                {/* FAQ List */}
                                <div className="space-y-3">
                                    {apiLoading ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-gray-200">
                                            <div className="loading-container">
                                                <div className="spinner"></div>
                                            </div>
                                            <p className="text-lg font-semibold text-gray-500 mt-4">Loading FAQs...</p>
                                        </div>
                                    ) : apiError ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-red-200 bg-red-50">
                                            <MessageCircleQuestion size={64} className="text-red-300" />
                                            <p className="text-lg font-semibold text-red-600 mt-4 mb-2">Error loading FAQs</p>
                                            <span className="text-sm text-red-500">{apiError.message}</span>
                                            <button
                                                onClick={() => fetchFaqs({
                                                    variables: {
                                                        accountId: accountId ? parseInt(accountId) : null,
                                                        roleId: roleId ? parseInt(roleId) : null,
                                                        searchTerm: faqSearchTerm || null,
                                                        faqModule: faqActiveTab,
                                                        pagingCriteria: {
                                                            limit: PAGE_SIZE,
                                                            page: currentPage,
                                                            sortOrder: "asc",
                                                            sortByField: "created_at"
                                                        }
                                                    }
                                                })}
                                                className="mt-4 px-4 py-2 bg-[#AD0101] text-white rounded-lg text-sm font-semibold hover:bg-[#b00000] transition-all"
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    ) : filteredFaqs.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-gray-200">
                                            <MessageCircleQuestion size={64} className="text-gray-300" />
                                            <p className="text-lg font-semibold text-gray-500 mt-4 mb-2">No FAQs found</p>
                                            <span className="text-base text-gray-400">Try a different search term or add a new FAQ</span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {filteredFaqs.map((faq, index) => (
                                                <div
                                                    key={faq.id}
                                                    className={`bg-white border-2 rounded-2xl overflow-hidden transition-all duration-300 ${expandedFaqId === faq.id
                                                        ? 'border-[#AD0101] shadow-lg shadow-red-100'
                                                        : 'border-gray-200 hover:border-[#AD0101] hover:shadow-md'
                                                        }`}
                                                >
                                                    <div
                                                        className="flex justify-between items-start p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                                                        onClick={() => setExpandedFaqId(expandedFaqId === faq.id ? null : faq.id)}
                                                    >
                                                        <div className="flex-1 pr-4">
                                                            <span className="text-[#AD0101] font-bold mr-2 text-lg">Q{((currentPage - 1) * PAGE_SIZE) + index + 1}.</span>
                                                            <span className="text-base font-medium text-gray-700 leading-relaxed">{faq.question}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            {/* Only show edit/delete if FAQ has valid accountId and roleIds */}
                                                            {faq.accountId && faq.roleIds && faq.roleIds.length > 0 && (
                                                                <>
                                                                    <Tooltip title="Edit">
                                                                        <button
                                                                            onClick={(e) => openEditModal(faq, e)}
                                                                            className="p-2 rounded-lg text-gray-400 hover:text-[#AD0101] hover:bg-red-50 transition-all duration-200"
                                                                        >
                                                                            <Edit3 size={18} />
                                                                        </button>
                                                                    </Tooltip>
                                                                    <Tooltip title="Delete">
                                                                        <button
                                                                            onClick={(e) => openDeleteConfirm(faq, e)}
                                                                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
                                                                        >
                                                                            <Trash2 size={18} />
                                                                        </button>
                                                                    </Tooltip>
                                                                </>
                                                            )}
                                                            <div className="text-[#AD0101] ml-2">
                                                                {expandedFaqId === faq.id ? (
                                                                    <ChevronUp size={24} />
                                                                ) : (
                                                                    <ChevronDown size={24} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {expandedFaqId === faq.id && (
                                                        <div className="px-5 pb-5 bg-gradient-to-b from-gray-50 to-white border-t border-dashed border-gray-200">
                                                            <div className="text-sm font-semibold text-[#AD0101] uppercase tracking-wide mt-4 mb-3">
                                                                Answer:
                                                            </div>
                                                            <p className="text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Pagination Controls */}
                                    {filteredFaqs.length > 0 && totalPages > 0 && (
                                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
                                            <div className="text-sm text-gray-600">
                                                {totalCount && <span>Showing {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} FAQs</span>}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={handlePreviousPage}
                                                    disabled={!hasPreviousPage}
                                                    className={`flex items-center justify-center w-9 h-9 rounded-lg text-sm font-medium transition-all ${!hasPreviousPage
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            : 'bg-white border border-gray-200 text-gray-700 hover:border-[#AD0101] hover:text-[#AD0101]'
                                                        }`}
                                                >
                                                    <ChevronLeft size={16} />
                                                </button>
                                                {(() => {
                                                    const pages = [];
                                                    const maxVisiblePages = 5;
                                                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                                                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                                                    if (endPage - startPage + 1 < maxVisiblePages) {
                                                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                                    }

                                                    if (startPage > 1) {
                                                        pages.push(
                                                            <button
                                                                key={1}
                                                                onClick={() => setCurrentPage(1)}
                                                                className="w-9 h-9 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:border-[#AD0101] hover:text-[#AD0101] transition-all"
                                                            >
                                                                1
                                                            </button>
                                                        );
                                                        if (startPage > 2) {
                                                            pages.push(<span key="start-ellipsis" className="px-2 text-gray-400">...</span>);
                                                        }
                                                    }

                                                    for (let i = startPage; i <= endPage; i++) {
                                                        pages.push(
                                                            <button
                                                                key={i}
                                                                onClick={() => setCurrentPage(i)}
                                                                className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${currentPage === i
                                                                        ? 'bg-[#AD0101] text-white'
                                                                        : 'bg-white border border-gray-200 text-gray-700 hover:border-[#AD0101] hover:text-[#AD0101]'
                                                                    }`}
                                                            >
                                                                {i}
                                                            </button>
                                                        );
                                                    }

                                                    if (endPage < totalPages) {
                                                        if (endPage < totalPages - 1) {
                                                            pages.push(<span key="end-ellipsis" className="px-2 text-gray-400">...</span>);
                                                        }
                                                        pages.push(
                                                            <button
                                                                key={totalPages}
                                                                onClick={() => setCurrentPage(totalPages)}
                                                                className="w-9 h-9 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:border-[#AD0101] hover:text-[#AD0101] transition-all"
                                                            >
                                                                {totalPages}
                                                            </button>
                                                        );
                                                    }

                                                    return pages;
                                                })()}
                                                <button
                                                    onClick={handleNextPage}
                                                    disabled={!hasNextPage}
                                                    className={`flex items-center justify-center w-9 h-9 rounded-lg text-sm font-medium transition-all ${!hasNextPage
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            : 'bg-white border border-gray-200 text-gray-700 hover:border-[#AD0101] hover:text-[#AD0101]'
                                                        }`}
                                                >
                                                    <ChevronRight size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add FAQ Modal */}
                <Modal
                    title={
                        <div className="flex items-center gap-3">
                            <span className="text-lg font-semibold">Add New FAQ</span>
                        </div>
                    }
                    open={showAddFaqModal}
                    onCancel={() => {
                        setShowAddFaqModal(false);
                        setNewFaq({ question: "", answer: "", product: "DARTS" });
                    }}
                    footer={null}
                    width={500}
                    style={{ top: 20 }}
                >
                    <div className="py-4">
                        {/* Product Selector */}
                        <div className="mb-3">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Product</label>
                            <div className="flex gap-3">
                                {productTabs.map((product) => (
                                    <div
                                        key={product}
                                        className={`flex flex-col items-center gap-2 px-5 py-3 border-2 rounded-xl cursor-pointer transition-all duration-300 flex-1 ${newFaq.product === product
                                            ? 'border-[#AD0101] bg-gradient-to-b from-red-50 to-red-100'
                                            : 'border-gray-200 hover:border-red-200 hover:bg-red-50'
                                            }`}
                                        onClick={() => setNewFaq(prev => ({ ...prev, product }))}
                                    >
                                        {/* <img src={getProductIcon(product)} alt={product} className="w-5 h-5 object-contain" /> */}
                                        <span className={`text-xs font-semibold ${newFaq.product === product ? 'text-[#AD0101]' : 'text-gray-600'}`}>
                                            {product}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Question Field */}
                        <div className="mb-3">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Question <span className="text-[#AD0101]">*</span>
                            </label>
                            <textarea
                                placeholder="Enter your question..."
                                value={newFaq.question}
                                onChange={(e) => setNewFaq(prev => ({ ...prev, question: e.target.value }))}
                                rows={3}
                                className="w-full p-3 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#AD0101] focus:ring-2 focus:ring-[#AD0101]/10 transition-all duration-300 resize-y"
                            />
                        </div>

                        {/* Answer Field */}
                        <div className="mb-3">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Answer <span className="text-[#AD0101]">*</span>
                            </label>
                            <textarea
                                placeholder="Enter the answer..."
                                value={newFaq.answer}
                                onChange={(e) => setNewFaq(prev => ({ ...prev, answer: e.target.value }))}
                                rows={5}
                                className="w-full p-3 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#AD0101] focus:ring-2 focus:ring-[#AD0101]/10 transition-all duration-300 resize-y"
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 justify-end">
                            <button
                                className="px-6 py-2.5 border-2 border-gray-200 bg-white rounded-lg text-sm font-medium text-gray-600 hover:border-[#AD0101] hover:text-[#AD0101] transition-all duration-300"
                                onClick={() => {
                                    setShowAddFaqModal(false);
                                    setNewFaq({ question: "", answer: "", product: "DARTS" });
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-300 ${!newFaq.question.trim() || !newFaq.answer.trim() || addFaqLoading
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-[#AD0101] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-200'
                                    }`}
                                onClick={handleAddFaq}
                                disabled={!newFaq.question.trim() || !newFaq.answer.trim() || addFaqLoading}
                            >
                                {addFaqLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Add FAQ'}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Edit FAQ Modal */}
                <Modal
                    title={
                        <div className="flex items-center gap-3">
                            <span className="text-lg font-semibold">Edit FAQ</span>
                        </div>
                    }
                    open={showEditFaqModal}
                    onCancel={() => {
                        setShowEditFaqModal(false);
                        setEditingFaq(null);
                    }}
                    footer={null}
                    width={500}
                    style={{ top: 40 }}
                >
                    {editingFaq && (
                        <div className="py-4">
                            {/* Current Product Badge */}
                            <div className="mb-5">
                                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-50 to-red-100 border-2 border-[#AD0101] rounded-xl w-fit">
                                    {/* <img src={getProductIcon(faqActiveTab)} alt={faqActiveTab} className="w-6 h-6 object-contain" /> */}
                                    <span className="text-sm font-semibold text-[#AD0101]">{faqActiveTab}</span>
                                </div>
                            </div>

                            {/* Question Field */}
                            <div className="mb-3">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Question <span className="text-[#AD0101]">*</span>
                                </label>
                                <textarea
                                    placeholder="Enter your question..."
                                    value={editingFaq.question}
                                    onChange={(e) => setEditingFaq(prev => ({ ...prev, question: e.target.value }))}
                                    rows={3}
                                    className="w-full p-3 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#AD0101] focus:ring-2 focus:ring-[#AD0101]/10 transition-all duration-300 resize-y"
                                />
                            </div>

                            {/* Answer Field */}
                            <div className="mb-3">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Answer <span className="text-[#AD0101]">*</span>
                                </label>
                                <textarea
                                    placeholder="Enter the answer..."
                                    value={editingFaq.answer}
                                    onChange={(e) => setEditingFaq(prev => ({ ...prev, answer: e.target.value }))}
                                    rows={5}
                                    className="w-full p-3 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#AD0101] focus:ring-2 focus:ring-[#AD0101]/10 transition-all duration-300 resize-y"
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 justify-end pt-3 border-t border-gray-100">
                                <button
                                    className="px-6 py-2.5 border-2 border-gray-200 bg-white rounded-lg text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-all duration-300"
                                    onClick={() => {
                                        setShowEditFaqModal(false);
                                        setEditingFaq(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={`px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-300 ${!editingFaq.question.trim() || !editingFaq.answer.trim() || editFaqLoading
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-[#AD0101] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-200'
                                        }`}
                                    onClick={handleEditFaq}
                                    disabled={!editingFaq.question.trim() || !editingFaq.answer.trim() || editFaqLoading}
                                >
                                    {editFaqLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    )}
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    title={
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 size={20} className="text-red-600" />
                            </div>
                            <span className="text-lg font-semibold text-gray-800">Delete FAQ</span>
                        </div>
                    }
                    open={showDeleteConfirm}
                    onCancel={() => {
                        setShowDeleteConfirm(false);
                        setDeletingFaq(null);
                    }}
                    footer={null}
                    width={450}
                    style={{ top: 40 }}
                >
                    <div className="py-4">
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete this FAQ?
                        </p>
                        {deletingFaq && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
                                <div className="text-sm font-semibold text-[#AD0101] mb-1">Question:</div>
                                <p className="text-sm text-[#AD0101]">{deletingFaq.question}</p>
                            </div>
                        )}
                        <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                            <button
                                className="px-6 py-2.5 border-2 border-gray-200 bg-white rounded-lg text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-all duration-300"
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDeletingFaq(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-300 ${deleteFaqLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#AD0101] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-200'}`}
                                onClick={handleDeleteFaq}
                                disabled={deleteFaqLoading}
                            >
                                {deleteFaqLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Delete FAQ'}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default AdminFAQPage;
